<template>
  <el-dialog
    :model-value="modelValue"
    title="项目导出"
    width="1120px"
    top="4vh"
    destroy-on-close
    @update:model-value="emit('update:modelValue', $event)"
    @closed="handleClosed"
  >
    <div class="project-export-dialog">
      <section class="export-workbench-shell">
        <div class="panel-header">
          <div>
            <h3>导出配置</h3>
            <p>{{ project?.name || '-' }}（{{ project?.code || '-' }}）· {{ normalizeVersion(project?.version) }}</p>
          </div>
          <div class="export-batch-actions">
            <el-button @click="generateTasks">生成 SQL</el-button>
            <el-button type="primary" :loading="runningAll" @click="runSelectedTasks">批量执行</el-button>
          </div>
        </div>

        <div class="config-grid">
          <div class="config-block">
            <label>导出时间</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              unlink-panels
              value-format="YYYY-MM-DD"
              range-separator="~"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              :shortcuts="datePickerShortcuts"
              :disabled-date="disableDate"
              style="width: 100%"
            />
          </div>

          <div class="config-block">
            <label>导出模式</label>
            <el-checkbox-group v-model="selectedModes">
              <el-checkbox v-for="item in modeOptions" :key="item.key" :label="item.key">
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </div>

          <div class="config-block">
            <label>导出内容</label>
            <el-checkbox-group v-model="selectedContents" class="content-grid">
              <el-checkbox v-for="item in contentOptions" :key="item.key" :label="item.key">
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </div>

          <div class="config-block">
            <label>附加选项</label>
            <div class="extra-config-grid">
              <el-checkbox v-model="byChannel">输出渠道明细</el-checkbox>
              <div class="stay-avg-config">
                <span>停留时长平均口径</span>
                <el-radio-group v-model="stayAvgBase" size="small">
                  <el-radio-button label="visitUv">除以访问 UV</el-radio-button>
                  <el-radio-button label="stayUv">除以停留时长 UV</el-radio-button>
                </el-radio-group>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="export-progress-shell" v-if="tasks.length">
        <div class="panel-header">
          <div>
            <h3>执行进度</h3>
            <p>已生成 {{ tasks.length }} 条 SQL，可执行项支持单独执行和批量执行；抽订单人群 SQL 仅用于复制。</p>
          </div>
          <div class="progress-summary">
            <span>已勾选 {{ enabledTaskCount }}</span>
            <span>成功 {{ successTaskCount }}</span>
            <span>失败 {{ errorTaskCount }}</span>
          </div>
        </div>
        <el-progress :percentage="overallProgress" :stroke-width="16" />
        <div class="task-batch-toolbar">
          <div class="task-batch-toolbar-hint">批量操作</div>
          <div class="task-batch-toolbar-actions">
            <el-button plain @click="toggleAllTasks(true)">全选</el-button>
            <el-button plain @click="toggleAllTasks(false)">清空</el-button>
            <el-button type="primary" plain @click="copySelectedSql">复制已勾选 SQL</el-button>
            <el-button type="success" @click="exportSelectedTaskResults">导出已执行结果</el-button>
          </div>
        </div>
      </section>

      <section class="export-task-list" v-if="tasks.length">
        <article v-for="task in tasks" :key="task.id" class="export-task-card">
          <div class="task-card-head">
            <div class="task-card-left">
              <el-checkbox v-model="task.enabled" />
              <div>
                <strong>{{ task.label }}</strong>
                <p>{{ task.copyOnly ? '复制专用 SQL' : task.statusText }}</p>
              </div>
            </div>
            <div class="task-card-actions">
              <el-button text @click="copySql(task.sql)">复制 SQL</el-button>
              <el-button text @click="resetTaskSql(task)">重置</el-button>
              <el-button text :disabled="task.copyOnly" :loading="task.running" @click="executeTask(task)">执行</el-button>
              <el-button text type="danger" :disabled="task.copyOnly || task.status !== 'error'" @click="retryTask(task)">重试</el-button>
              <el-button text :disabled="task.copyOnly || !task.resultRows.length" @click="exportTaskResult(task)">导出结果</el-button>
            </div>
          </div>

          <el-input
            v-model="task.sql"
            type="textarea"
            :rows="task.expanded ? 16 : 8"
            resize="vertical"
            class="task-sql-editor"
          />

          <div class="task-card-meta">
            <el-button text @click="task.expanded = !task.expanded">
              {{ task.expanded ? '收起 SQL' : '展开 SQL' }}
            </el-button>
            <el-tag size="small" :type="task.copyOnly ? 'info' : task.statusType">{{ task.copyOnly ? '仅复制' : task.statusText }}</el-tag>
          </div>

          <div v-if="task.errorMessage" class="task-error-box">
            {{ task.errorMessage }}
          </div>

          <div v-if="!task.copyOnly && task.resultRows.length" class="task-preview-shell">
            <div class="panel-header">
              <div>
                <h3>结果预览</h3>
                <p>共 {{ task.resultRows.length }} 行</p>
              </div>
            </div>
            <el-table :data="task.resultRows" stripe max-height="260">
              <el-table-column
                v-for="column in task.columns"
                :key="column"
                :prop="column"
                :label="column"
                min-width="140"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  {{ formatPreviewCell(row, column) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </article>
      </section>

      <section v-else class="empty-task-shell">
        <el-empty description="先选择导出内容和模式，再生成 SQL" />
      </section>
    </div>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import api from '@/utils/http'
import { buildExportFileName, buildGroupedDateReportSheet, buildTableSheetFromRows, exportWorkbook } from '../reportDataExport'
import {
  buildProjectExportTasks,
  PROJECT_EXPORT_CONTENTS,
  PROJECT_EXPORT_MODES
} from './projectExportSqlBuilders'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  project: {
    type: Object,
    default: null
  },
  defaultRange: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue'])

const contentOptions = PROJECT_EXPORT_CONTENTS
const modeOptions = PROJECT_EXPORT_MODES

const dateRange = ref([])
const selectedContents = ref(['flow', 'click', 'stayTime'])
const selectedModes = ref(['dailySum'])
const byChannel = ref(false)
const stayAvgBase = ref('visitUv')
const tasks = ref([])
const runningAll = ref(false)

const overallProgress = computed(() => {
  if (!tasks.value.length) return 0
  const completed = tasks.value.filter((item) => item.status === 'success' || item.status === 'error').length
  return Math.round((completed / tasks.value.length) * 100)
})
const enabledTaskCount = computed(() => tasks.value.filter((item) => item.enabled).length)
const successTaskCount = computed(() => tasks.value.filter((item) => item.status === 'success').length)
const errorTaskCount = computed(() => tasks.value.filter((item) => item.status === 'error').length)

const datePresetOptions = [
  { label: '昨天', value: 'yesterday' },
  { label: '近3天', value: 'last3' },
  { label: '近7天', value: 'last7' },
  { label: '近1个月', value: 'last30' },
  { label: '近3个月', value: 'last90' }
]

const datePickerShortcuts = datePresetOptions.map((item) => ({
  text: item.label,
  value: () => {
    const [start, end] = getPresetRange(item.value)
    return [new Date(start), new Date(end)]
  }
}))

watch(
  () => props.modelValue,
  (visible) => {
    if (!visible) return
    initializeState()
  },
  { immediate: true }
)

watch(dateRange, () => {
  if (!tasks.value.length) return
  regenerateTasksForCurrentConfig()
}, { deep: true })

function initializeState() {
  dateRange.value = Array.isArray(props.defaultRange) && props.defaultRange.length ? [...props.defaultRange] : getDefaultRange()
  selectedContents.value = ['flow', 'click', 'stayTime']
  selectedModes.value = ['dailySum']
  byChannel.value = false
  stayAvgBase.value = 'visitUv'
  tasks.value = []
}

function handleClosed() {
  initializeState()
}

function disableDate(date) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() >= today.getTime()
}

function normalizeVersion(version) {
  return String(version || 'v4').toLowerCase() === 'v3' ? 'V3' : 'V4'
}

function generateTasks() {
  const generated = buildTasksFromCurrentConfig()
  if (!generated) return
  tasks.value = generated
}

function buildTasksFromCurrentConfig() {
  if (!props.project?.code) {
    ElMessage.warning('缺少项目信息')
    return null
  }
  if (!dateRange.value?.[0] || !dateRange.value?.[1]) {
    ElMessage.warning('请选择导出日期')
    return null
  }
  if (!selectedContents.value.length) {
    ElMessage.warning('请至少勾选一个导出内容')
    return null
  }
  if (!selectedModes.value.length && !selectedContents.value.includes('orderCrowd')) {
    ElMessage.warning('请至少勾选一个导出模式')
    return null
  }

  const generated = buildProjectExportTasks({
    project: props.project,
    startDate: dateRange.value[0],
    endDate: dateRange.value[1],
    selectedContents: selectedContents.value,
    selectedModes: selectedModes.value,
    byChannel: byChannel.value,
    stayAvgBase: stayAvgBase.value
  })

  return generated.map((item) => reactive({
    ...item,
    originalSql: item.sql,
    enabled: true,
    expanded: false,
    running: false,
    status: 'idle',
    statusText: '待执行',
    statusType: 'info',
    errorMessage: '',
    resultRows: [],
    columns: []
  }))
}

function regenerateTasksForCurrentConfig() {
  const previousStateMap = new Map(
    tasks.value.map((item) => [`${item.contentKey}_${item.mode}`, item])
  )
  const generated = buildTasksFromCurrentConfig()
  if (!generated) return
  tasks.value = generated.map((task) => {
    const previous = previousStateMap.get(`${task.contentKey}_${task.mode}`)
    if (!previous) return task
    task.enabled = previous.enabled
    task.expanded = previous.expanded
    return task
  })
  ElMessage.success('导出时间已变更，SQL 已自动重新生成')
}

watch([byChannel, stayAvgBase, selectedModes, selectedContents], () => {
  if (!tasks.value.length) return
  regenerateTasksForCurrentConfig()
}, { deep: true })

async function runSelectedTasks() {
  const enabledTasks = tasks.value.filter((item) => item.enabled && !item.copyOnly)
  if (!enabledTasks.length) {
    ElMessage.warning('请至少勾选一条可执行 SQL')
    return
  }
  runningAll.value = true
  try {
    for (const task of enabledTasks) {
      await executeTask(task)
    }
  } finally {
    runningAll.value = false
  }
}

function toggleAllTasks(checked) {
  tasks.value.forEach((item) => {
    item.enabled = checked
  })
}

async function executeTask(task) {
  if (task.copyOnly) {
    ElMessage.info('这条 SQL 仅用于复制，不需要执行')
    return
  }
  task.running = true
  task.status = 'running'
  task.statusText = '执行中'
  task.statusType = 'warning'
  task.errorMessage = ''
  try {
    const result = await api.executeDataWorksSql(task.sql)
    if (result?.success === false) {
      throw new Error(result?.errorMsg || result?.message || 'SQL 执行失败')
    }
    const rows = Array.isArray(result?.data) ? result.data : []
    task.resultRows = rows
    task.columns = rows.length ? Object.keys(rows[0]) : []
    task.status = 'success'
    task.statusText = `执行成功${rows.length ? `，${rows.length} 行` : '，无结果'}`
    task.statusType = 'success'
  } catch (error) {
    task.status = 'error'
    task.statusText = '执行失败'
    task.statusType = 'danger'
    task.errorMessage = error.message || 'SQL 执行失败'
  } finally {
    task.running = false
  }
}

async function retryTask(task) {
  await executeTask(task)
}

function resetTaskSql(task) {
  task.sql = task.originalSql
}

async function copySql(sql) {
  try {
    await navigator.clipboard.writeText(sql)
    ElMessage.success('SQL 已复制')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

async function copySelectedSql() {
  const enabledTasks = tasks.value.filter((item) => item.enabled)
  if (!enabledTasks.length) {
    ElMessage.warning('请先勾选 SQL')
    return
  }
  const text = enabledTasks
    .map((item) => `-- ${item.label}\n${item.sql}`)
    .join('\n\n')
  await copySql(text)
}

function exportTaskResult(task) {
  if (!task.resultRows.length) {
    ElMessage.warning('请先执行 SQL 获取结果')
    return
  }
  const sheet = buildSingleTaskReportSheet(task)
  exportWorkbook({
    sheets: [sheet],
    fileName: buildExportFileName({
      projectName: props.project?.name,
      projectCode: props.project?.code,
      dataLabel: task.label,
      startDate: dateRange.value?.[0] || 'start',
      endDate: dateRange.value?.[1] || 'end'
    })
  })
}

function exportSelectedTaskResults() {
  const readyTasks = tasks.value.filter((item) => item.enabled && !item.copyOnly && item.resultRows.length)
  if (!readyTasks.length) {
    ElMessage.warning('请先执行并勾选至少一条结果')
    return
  }
  const metricTasks = readyTasks.filter((item) => item.contentKey !== 'orderCrowd')
  const orderCrowdTasks = readyTasks.filter((item) => item.contentKey === 'orderCrowd')
  const sheets = []
  if (metricTasks.length) {
    sheets.push(buildCombinedReportSheet(metricTasks))
  }
  orderCrowdTasks.forEach((item) => {
    sheets.push(
      buildTableSheetFromRows({
        sheetName: item.label,
        rows: item.resultRows,
        themeKey: 'ocean'
      })
    )
  })
  exportWorkbook({
    sheets,
    fileName: buildExportFileName({
      projectName: props.project?.name,
      projectCode: props.project?.code,
      dataLabel: '项目导出结果',
      startDate: dateRange.value?.[0] || 'start',
      endDate: dateRange.value?.[1] || 'end'
    })
  })
}

function buildSingleTaskReportSheet(task) {
  if (task.copyOnly || task.contentKey === 'orderCrowd') {
    return buildTableSheetFromRows({
      sheetName: task.label,
      rows: task.resultRows,
      themeKey: 'ocean'
    })
  }

  const report = buildTaskReportColumns([task])
  return buildGroupedDateReportSheet({
    sheetName: task.label,
    dateList: report.dateList,
    groups: report.groups,
    includeDailySumRow: task.includeDailySum,
    includeDedupRow: task.includeDedup,
    themeKey: 'ocean'
  })
}

function buildCombinedReportSheet(taskList) {
  const report = buildTaskReportColumns(taskList)
  const includeDailySumRow = taskList.some((item) => item.includeDailySum)
  const includeDedupRow = taskList.some((item) => item.includeDedup)
  return buildGroupedDateReportSheet({
    sheetName: '项目导出报表',
    dateList: report.dateList,
    groups: report.groups,
    includeDailySumRow,
    includeDedupRow,
    themeKey: 'ocean'
  })
}

function buildTaskReportColumns(taskList) {
  const dateList = buildDateList(dateRange.value?.[0], dateRange.value?.[1])
  const groups = []

  taskList.forEach((task) => {
    const metrics = resolveTaskMetrics(task.resultRows).map((key) => ({
      key,
      label: resolveMetricLabel(key),
      format: resolveMetricFormat(key)
    }))
    const dailyRows = task.resultRows.filter((row) => row.time_scope && row.time_scope !== '__dedup__')
    const dedupRows = task.resultRows.filter((row) => row.time_scope === '__dedup__')
    const subgroupKeys = Array.from(new Set(dailyRows.map((row) => resolveTaskGroupLabel(row)))).sort((a, b) => a.localeCompare(b, 'zh-CN'))

    subgroupKeys.forEach((subgroup) => {
      const label = subgroup === task.label || subgroup === (dailyRows.find((row) => resolveTaskGroupLabel(row) === subgroup)?.title)
        ? task.label
        : `${task.label}-${subgroup}`
      const valuesByDate = {}
      dateList.forEach((date) => {
        valuesByDate[date] = createMetricValueShell(metrics.map((item) => item.key))
      })

      dailyRows
        .filter((row) => resolveTaskGroupLabel(row) === subgroup)
        .forEach((row) => {
          metrics.forEach((metric) => {
            valuesByDate[row.time_scope][metric.key] = toNumber(row[metric.key])
          })
        })

      const dailySum = createMetricValueShell(metrics.map((item) => item.key))
      dateList.forEach((date) => {
        metrics.forEach((metric) => {
          dailySum[metric.key] += toNumber(valuesByDate[date][metric.key])
        })
      })

      const dedupRow = dedupRows.find((row) => resolveTaskGroupLabel(row) === subgroup) || {}
      const dedup = createMetricValueShell(metrics.map((item) => item.key))
      metrics.forEach((metric) => {
        dedup[metric.key] = toNumber(dedupRow[metric.key])
      })

      groups.push({
        label,
        metrics,
        valuesByDate,
        summary: {
          dailySum,
          dedup
        }
      })
    })
  })

  return { dateList, groups }
}

function resolveTaskMetrics(rows) {
  const metaKeys = new Set(['title', 'time_scope', 'channel', 'index_name', 'code'])
  const first = rows[0] || {}
  return Object.keys(first).filter((key) => !metaKeys.has(key))
}

function resolveTaskGroupLabel(row) {
  if (row.channel && row.index_name) return `${row.channel}-${row.index_name}`
  if (row.channel) return row.channel
  if (row.index_name) return row.index_name
  return row.title || '数据'
}

function resolveMetricLabel(key) {
  const map = {
    pv: 'PV',
    uv: 'UV',
    timeOnSite: '停留时长',
    totalTimeOnSite: '总停留时长',
    avg_stay_seconds: '平均停留秒'
  }
  return map[key] || key
}

function resolveMetricFormat(key) {
  if (['avg_stay_seconds', 'clickRate', 'dropRate'].includes(key)) return 'decimal'
  if (['pv', 'uv', 'timeOnSite', 'totalTimeOnSite'].includes(key)) return 'integer'
  return 'integer'
}

function createMetricValueShell(metrics) {
  return metrics.reduce((acc, metric) => {
    acc[metric] = 0
    return acc
  }, {})
}

function formatPreviewCell(row, column) {
  const value = row?.[column]
  if (value === null || value === undefined || value === '') return ''
  if (isNumericLike(value)) {
    const format = resolveMetricFormat(column)
    return formatDisplayNumber(value, format)
  }
  return value
}

function isNumericLike(value) {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value !== 'string') return false
  const text = value.trim()
  if (!text) return false
  return Number.isFinite(Number(text))
}

function formatDisplayNumber(value, format = 'integer') {
  const num = Number(value)
  if (!Number.isFinite(num)) return value
  if (format === 'decimal') {
    return new Intl.NumberFormat('zh-CN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num)
  }
  return new Intl.NumberFormat('zh-CN', {
    maximumFractionDigits: 0
  }).format(num)
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function buildDateList(startDate, endDate) {
  const list = []
  const cursor = new Date(startDate)
  const end = new Date(endDate)
  while (cursor <= end) {
    list.push(formatDate(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return list
}

function getDefaultRange() {
  const end = getRelativeDate(-1)
  const start = new Date(end)
  start.setDate(start.getDate() - 6)
  return [formatDate(start), formatDate(end)]
}

function getPresetRange(preset) {
  const end = getRelativeDate(-1)
  const start = new Date(end)
  const sizeMap = {
    yesterday: 1,
    last3: 3,
    last7: 7,
    last30: 30,
    last90: 90
  }
  const days = sizeMap[preset] || 7
  start.setDate(start.getDate() - days + 1)
  return [formatDate(start), formatDate(end)]
}

function getRelativeDate(offsetDays) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return date
}

function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
</script>

<style scoped>
.project-export-dialog {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 78vh;
  overflow: auto;
  padding-right: 4px;
}

.export-workbench-shell,
.export-progress-shell,
.export-task-card,
.empty-task-shell {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 16px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.config-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.config-block label {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.content-grid,
.extra-config-grid {
  display: grid;
  gap: 10px;
}

.content-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stay-avg-config {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stay-avg-config span {
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

.export-batch-actions,
.task-card-actions,
.task-card-meta,
.task-card-left,
.task-batch-toolbar,
.progress-summary {
  display: flex;
}

.export-batch-actions,
.task-card-actions {
  gap: 8px;
}

.task-batch-toolbar,
.progress-summary {
  gap: 12px;
  flex-wrap: wrap;
}

.task-batch-toolbar {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
  align-items: center;
  justify-content: space-between;
}

.progress-summary {
  color: #64748b;
  font-size: 12px;
}

.task-batch-toolbar-hint {
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.task-batch-toolbar-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.task-batch-toolbar-actions :deep(.el-button) {
  min-width: 120px;
}

.task-card-left {
  gap: 10px;
  align-items: flex-start;
}

.task-card-left strong {
  color: #0f172a;
}

.task-card-left p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.export-task-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.task-card-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.task-sql-editor {
  margin-bottom: 10px;
}

.task-card-meta {
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.task-error-box {
  margin-bottom: 10px;
  border-radius: 8px;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #be123c;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.6;
}

.task-preview-shell {
  margin-top: 12px;
}

@media (max-width: 960px) {
  .config-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .task-card-head {
    flex-direction: column;
  }

  .task-card-actions,
  .task-batch-toolbar-actions {
    flex-wrap: wrap;
  }

  .task-batch-toolbar {
    align-items: stretch;
  }
}
</style>
